/**
 * @fileoverview Data hooks shared by the three power bank screens.
 */
import { useCallback, useEffect, useState } from 'react'

import {
  getCityCurrency,
  getCityPowerBankHolds,
  getPowerBankHoldSetting,
} from 'Module/graphQL'

/**
 * Reads a fetch Response from callAuthorizedGraphQLEndpoint and returns its
 * `data`, throwing the first GraphQL error as an Error so callers can show
 * `error.message` next to the button that was pressed.
 * @param {Response} response
 * @returns {Promise<object>}
 */
export const readGraphQLData = async response => {
  const result = await response.json()
  if (result.errors && result.errors[0]) {
    throw new Error(result.errors[0].message)
  }
  return result.data
}

const EMPTY_MONEY = { currencySign: null, currency: null, countryCode: null }

/**
 * The currency sign and ISO code of the city's country. Hold amounts are
 * always rendered with these, never with a hardcoded sign.
 * @param {string|number} cityId
 * @returns {{money: {currencySign: ?string, currency: ?string}, loading: boolean}}
 */
export const useCityCurrency = cityId => {
  const [money, setMoney] = useState(EMPTY_MONEY)
  const [loading, setLoading] = useState(Boolean(cityId))

  useEffect(() => {
    if (!cityId) return undefined
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const data = await readGraphQLData(await getCityCurrency(cityId))
        const country = (data.city && data.city.country) || {}
        if (cancelled) return
        setMoney({
          currencySign: country.currencySign || null,
          currency: country.currencyName || null,
          countryCode: country.code || null,
        })
      } catch (error) {
        // The sign is cosmetic: the amount still renders with the ISO code
        // the hold itself carries. Log so a broken city is noticed.
        // eslint-disable-next-line no-console
        console.error('Could not load the city currency', error)
        if (!cancelled) setMoney(EMPTY_MONEY)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [cityId])

  return { money, loading }
}

const DEFAULT_SETTING = { holdRequired: false, holdAmount: 0, currency: null }

/**
 * The city's default power bank hold ("hold Y/N" and "how much").
 * @param {string|number} cityId
 * @returns {{setting: object, loading: boolean, error: ?string,
 * setSetting: function, reload: function}}
 */
export const usePowerBankHoldSetting = cityId => {
  const [setting, setSetting] = useState(DEFAULT_SETTING)
  const [loading, setLoading] = useState(Boolean(cityId))
  const [error, setError] = useState(null)

  const reload = useCallback(async () => {
    if (!cityId) return
    setLoading(true)
    setError(null)
    try {
      const data = await readGraphQLData(await getPowerBankHoldSetting(cityId))
      setSetting({ ...DEFAULT_SETTING, ...(data.powerBankHoldSetting || {}) })
    } catch (loadError) {
      setError(loadError.message)
    } finally {
      setLoading(false)
    }
  }, [cityId])

  useEffect(() => {
    reload()
  }, [reload])

  return { setting, loading, error, setSetting, reload }
}

/**
 * Every live power bank hold in the city, keyed by inventory id, so the
 * pickup page can print the amount and whether the BA agreed next to each
 * unit that is out. `reload` is called after every handover, return and
 * write-off so the labels never go stale.
 * @param {string|number} cityId
 * @returns {{holdsByInventoryId: Object, loading: boolean, reload: function}}
 */
export const useCityPowerBankHolds = cityId => {
  const [holdsByInventoryId, setHolds] = useState({})
  const [loading, setLoading] = useState(Boolean(cityId))

  const reload = useCallback(async () => {
    if (!cityId) return
    setLoading(true)
    try {
      const data = await readGraphQLData(await getCityPowerBankHolds(cityId))
      const byUnit = {}
      ;(data.cityPowerBankHolds || []).forEach(hold => {
        byUnit[String(hold.inventoryId)] = hold
      })
      setHolds(byUnit)
    } catch (error) {
      // The labels are informational; the handover itself does not depend on
      // them. Log so a broken query is noticed.
      // eslint-disable-next-line no-console
      console.error('Could not load the city power bank holds', error)
    } finally {
      setLoading(false)
    }
  }, [cityId])

  useEffect(() => {
    reload()
  }, [reload])

  return { holdsByInventoryId, loading, reload }
}
